// Step23_DOTHours.js — "Have you logged DOT hours in the last seven days?"
//
// This is the data behind a certification the driver is ALREADY signing. The
// signature step lists "Drivers Statement of On-Duty Hours for Newly Hired
// Drivers ... 49 CFR § 395.8(j)(2)" among the documents authorised. Until this
// step existed, that statement was signed with nothing behind it.
//
// Two rules this screen exists to enforce:
//
//   1. NO still writes seven zeros. It does not skip the block. A skipped
//      block is indistinguishable from a block nobody ever answered, and the
//      file has to positively record that the driver was asked and said no.
//
//   2. The total is COMPUTED. The driver never types it, so it can never
//      disagree with his own seven days.
//
// The window is stamped once, on arrival, and never recomputed. See
// utils/dotHours.js for why.

import React, { useEffect, useState } from 'react';
import { useForm } from '../context/FormContext';
import {
  buildHoursWindow,
  formatDayLabel,
  totalHours,
  isValidHourEntry,
  localISODate,
  DOT_HOURS_MAX_PER_DAY,
} from '../utils/dotHours';

export default function Step23_DOTHours({ onNext, onBack }) {
  const { state, dispatch } = useForm();
  const dh = state.dotHours;
  const [errs, setErrs] = useState({});

  // Stamp the window ONCE. Coming back through Edit must not re-anchor it.
  useEffect(() => {
    if (!dh.days || dh.days.length !== 7) {
      const now = new Date();
      dispatch({
        type: 'SET_DOT_HOURS',
        value: { days: buildHoursWindow(now), anchoredAt: localISODate(now) },
      });
    }
    // Mount only, on purpose: re-running this on every change is exactly the
    // re-anchoring the frozen window exists to prevent.
  }, []); // eslint-disable-line

  const days = dh.days || [];
  const total = totalHours(days);

  const setAnswer = (val) => {
    if (val === 'no') {
      // Explicit zeros, not an absent block.
      dispatch({
        type: 'SET_DOT_HOURS',
        value: { answer: 'no', days: days.map(d => ({ ...d, hours: 0 })) },
      });
    } else {
      dispatch({ type: 'SET_DOT_HOURS', value: { answer: 'yes' } });
    }
    setErrs({});
  };

  const setHours = (idx, raw) => {
    if (!isValidHourEntry(raw)) return;   // reject the keystroke outright
    const s = String(raw).trim();
    const next = days.map((d, i) =>
      i === idx ? { ...d, hours: s === '' ? 0 : Number(s) } : d);
    dispatch({ type: 'SET_DOT_HOURS', value: { days: next } });
    setErrs(e => ({ ...e, grid: '' }));
  };

  const next = () => {
    const e = {};
    if (!dh.answer) {
      e.answer = 'Please select Yes or No';
      setErrs(e);
      return;
    }
    if (days.length !== 7) {
      e.grid = 'Please reload the page — the seven-day window did not load.';
      setErrs(e);
      return;
    }
    if (dh.answer === 'yes' && total === 0) {
      e.grid = 'You answered Yes — enter the hours you logged on at least one day.';
      setErrs(e);
      return;
    }
    setErrs({});
    onNext();
  };

  return (
    <div className="step-card">
      <h2 className="step-title">On-Duty Hours</h2>
      <p style={{ fontSize: 17, fontWeight: 600, lineHeight: 1.5, marginBottom: 8, color: 'var(--text)' }}>
        Have you logged DOT hours in the last seven days?
      </p>
      <p className="step-subtitle" style={{ marginBottom: 20 }}>
        Required by 49 CFR § 395.8(j)(2). Whole hours only.
      </p>

      <div className="yn-buttons">
        <button className={`yn-btn yes ${dh.answer === 'yes' ? 'selected' : ''}`} onClick={() => setAnswer('yes')}>YES</button>
        <button className={`yn-btn no ${dh.answer === 'no' ? 'selected' : ''}`} onClick={() => setAnswer('no')}>NO</button>
      </div>
      {errs.answer && <div className="field-error" style={{ marginTop: -8, marginBottom: 12 }}>{errs.answer}</div>}

      {dh.answer === 'yes' && (
        <div style={{ marginTop: 20 }}>
          <label className="field-label" style={{ marginBottom: 10, display: 'block' }}>
            Hours on duty each day <span className="required">*</span>
          </label>

          {days.map((d, i) => (
            <div key={d.date} style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '8px 0',
              borderBottom: i < days.length - 1 ? '1px solid var(--border)' : 'none',
            }}>
              <span style={{ fontSize: 15, color: 'var(--text)' }}>
                {formatDayLabel(d.date)}
                {i === days.length - 1 && (
                  <span style={{ fontSize: 13, color: 'var(--text-light)' }}> (today)</span>
                )}
              </span>
              <input
                className="field-input"
                style={{ width: 84, textAlign: 'center', marginBottom: 0 }}
                type="tel"
                inputMode="numeric"
                maxLength={2}
                value={d.hours === 0 ? '' : String(d.hours)}
                onChange={ev => setHours(i, ev.target.value)}
                placeholder="0"
                aria-label={`Hours on ${d.date}`}
              />
            </div>
          ))}

          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: 14, paddingTop: 14, borderTop: '2px solid var(--border)',
          }}>
            <span style={{ fontSize: 16, fontWeight: 700 }}>Total</span>
            <span style={{ fontSize: 20, fontWeight: 700 }}>{total}</span>
          </div>
          <p style={{ fontSize: 13, color: 'var(--text-light)', marginTop: 6 }}>
            Total is calculated from the days above. Maximum {DOT_HOURS_MAX_PER_DAY} hours per day.
          </p>

          {errs.grid && <div className="field-error" style={{ marginTop: 10 }}>{errs.grid}</div>}
        </div>
      )}

      {dh.answer === 'no' && (
        <p style={{ fontSize: 14, color: 'var(--text-light)', marginTop: 18, lineHeight: 1.5 }}>
          All seven days will be recorded as zero hours.
        </p>
      )}

      <div className="wizard-nav" style={{ position: 'static', borderTop: 'none', padding: 0, marginTop: 24 }}>
        <button className="btn-back" onClick={onBack}>← Back</button>
        <button className="btn-next" onClick={next}>Next →</button>
      </div>
    </div>
  );
}
